import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AppConfig } from 'src/app/app.config';
import { TreatmentProgressAddDialog } from 'src/components/treatment-progress-add/treatment-progress-add.component';
import { ConnectionService } from '../../../../../services/connection-service';
import { VoiceNoteAddDialog } from '../../../../../components/voice-note-add/voice-note-add.component';
import { InputType } from 'typings/client/input';
import { ApiClientConstant } from '@cureskin/api-client';
import { RegimenType } from '../../../../../../typings/client/regimen';

declare type FollowUpSOPNode = {
  name?: string;
  shortName?: string;
  tests?: any;
  audioTemplateName?: string;
  treatmentProgress?: Array<{ issue: string, concernLanguageString: any, score: number }>;
  disabled?: boolean;
  nextFollowUps: Array<FollowUpSOPNode>,
  changes: Array<{ partOfDay?: string; type?: string; onHold?: boolean; isComplementory?: boolean;
    position?: any; productId?: string, onHoldDurationInDays?: number;
    instruction?: Record<string, unknown>; }>;
};

@Component({
    selector: 'followup-sop',
    templateUrl: './followup-sop.html',
    styleUrls: ['./followup-sop.scss'],
    viewProviders: [{
      provide: ControlContainer,
      useExisting: NgForm,
    }],
    standalone: false
})
export class FollowUpSOPComponent implements OnInit {
  @Input('node') node: FollowUpSOPNode;
  @Input('prefix') prefix: string = '';
  @Input('regimenId') regimenId: string;
  @Input() variantId: string;
  @Input('level')
  set levelValue(level: number) {
    this.level = level;
    this.getRandomColor();
  }
  level: number = 0;
  bgColor: string = '#ffffff';
  regimenProducts: any = [];
  productInfoList: Array<RegimenType.Product> = [];
  nameOptions: Array<InputType.SelectOption> = [];
  @Output() formValue: EventEmitter<any> = new EventEmitter();
  @Output() delete: EventEmitter<any> = new EventEmitter();
  testsList: any = [];
  treatmentProgress?: Array<{ issue: string, concernLanguageString: any, score: number }> = [];
  class: string;
  operationChange: any = ['addProduct', 'removeProduct', 'changeInstruction'];
  selectedInvestigation: { tests: { test: string, required: boolean }[], name?: string, index?: number } = { tests: [] };
  shortNameOptions: Array<{ display: string, value: string }> = [
    { display: 'Change as expected', value: 'CHANGE_AS_EXPECTED' },
    { display: 'Slow improvement', value: 'SLOW_IMPROVEMENT' },
    { display: 'No change', value: 'NO_CHANGE' },
    { display: 'Dis-comfort', value: 'DISCOMFORT' },
    { display: 'Slightly increased', value: 'SLIGHTLY_INCREASED' },
    { display: 'Flare up', value: 'FLARE_UP' },
    { display: 'others', value: 'Others' },
  ];
  showShortNameTextInput: boolean;
  @Input('prevLevelRegimen') prevLevelRegimen: any;
  constructor(private conn: ConnectionService,
    private router: ActivatedRoute,
    private appConfig: AppConfig,
    private dialog: MatDialog) { }

  async ngOnInit(): Promise<void> {
    this.initProductInfoList();
    await this.initRegimenProducts();
    await this.loadFormOptions();
  }

  private initProductInfoList(): void {
    this.productInfoList = this.node.changes.map((change: any): RegimenType.Product => {
      const info: RegimenType.Product = change.productInfo || {};
      if (!(info as any).instructionSet && change.instruction) {
        (info as any).instructionSet = change.instruction;
      }
      return info;
    });
    this.node.changes.forEach((change: any): void => { delete change.productInfo; });
  }

  private async initRegimenProducts(): Promise<void> {
    const [regimen]: Array<any> = await this.conn.findRegimens({ where: { regimenId: this.regimenId }, project: ['products', 'class'], include: ['products'] });
    this.class = regimen.get('class');
    this.regimenProducts = [...regimen.get('products')];
    if (!this.prevLevelRegimen?.length) {
      this.prevLevelRegimen = this.regimenProducts;
    }
    await this.buildPrevLevelRegimen();
    this.regimenProducts = [...this.prevLevelRegimen];
  }

  private async buildPrevLevelRegimen(): Promise<void> {
    for (const [i, change] of this.node.changes.entries()) {
      const product = await this.resolveProduct(i, change);
      if (product && change.type === 'addProduct') {
        this.prevLevelRegimen.push(product);
      }
    }
  }

  private async resolveProduct(index: number, change: any): Promise<any> {
    if (this.productInfoList[index]?.product) {
      return this.productInfoList[index].product;
    }
    if (!change.productId) return null;
    const [catalog]: Array<any> = await this.conn.findCatalogs({ where: { objectId: change.productId }, limit: 1 });
    if (catalog) {
      this.productInfoList[index] = { ...this.productInfoList[index], product: catalog };
    }
    return catalog ?? null;
  }

  private async loadFormOptions(): Promise<void> {
    this.testsList = await this.conn.fetchInvestigations();
    this.selectedInvestigation.index = this.testsList
      .findIndex((each: any): boolean => each.get('name') === this.node?.tests?.name);
    this.getRandomColor();
    this.nameOptions = await this.conn.getTreatmentScenarioOptions(`F${this.level + 1}`);
    this.node.changes.forEach((each): void => {
      if (each.onHold && !each.onHoldDurationInDays) {
        each.onHoldDurationInDays = 7;
      }
    });
  }

  onChangeShortName(): void {
    if (this.node.shortName !== 'Others') {
      this.showShortNameTextInput = false;
      return;
    }
    delete this.node.shortName;
    this.showShortNameTextInput = true;
  }

  changeInvestigation(): void {
    const selectedIndex = Number(this.selectedInvestigation.index);
    if (selectedIndex >= 0) this.selectedInvestigation.name = this.testsList[selectedIndex].get('name');
    if (selectedIndex >= 0) {
      this.selectedInvestigation.tests = this.testsList[selectedIndex].get('tests');
      this.node.tests = this.selectedInvestigation;
    } else {
      this.selectedInvestigation.tests = [];
      delete this.selectedInvestigation.name;
    }
  }

  addFollowUpForm(): any {
    this.node.nextFollowUps.push({ nextFollowUps: [], changes: [], treatmentProgress: [] });
  }

  addRegimenChanges(): any {
    this.node.changes.push({
      type: '',
      partOfDay: '',
      onHold: false,
      isComplementory: false,
      position: undefined,
      productId: '',
      instruction: {},
    });
    this.productInfoList.push({});
  }

  deleteFollowUp(index: any): any {
    this.node.nextFollowUps.splice(index, 1);
  }

  removeRegimenChange(index: number): any {
    this.node.changes.splice(index, 1);
    this.productInfoList.splice(index, 1);
  }

  getRandomColor(): void {
    const depthColorCode = ['#D3C6DD', '#EDE8F1', '#B6CCE7', '#BDDDD4', '#ECE089', '#F1C49C', '#F1149C'];
    this.bgColor = depthColorCode[this.level];
  }

  removeFollowUp(): any {
    this.delete.emit();
  }

  regimenChange(index: number): any {
    const productData = this.productInfoList[index];
    if (this.node.changes[index].type === 'addProduct') {
      this.prevLevelRegimen.push(productData?.product);
    }
    if (this.node.changes[index].type === 'removeProduct') {
      const arrCopy = Array.from(this.prevLevelRegimen);
      const objWithIdIndex = arrCopy.findIndex((obj: any) => obj.id === productData?.product?.id);
      arrCopy.splice(objWithIdIndex, 1);
      this.prevLevelRegimen = arrCopy;
    }
    this.node.changes[index].productId = productData?.product?.id;
    this.node.changes[index].instruction = productData?.instructionSet;
  }

  async treatmentProgressEdit(): Promise<void> {
    if (!this.node.treatmentProgress) {
      this.node.treatmentProgress = [];
    }
    this.dialog.open(TreatmentProgressAddDialog, { data:
      { treatmentProgress: this.node.treatmentProgress, class: this.class } }).afterClosed().subscribe((result: any) => {
      if (result?.issue) {
        this.node.treatmentProgress.push({ issue: result.issue, concernLanguageString: result.concernLanguageString, score: result.score });
      }
    });
  }

  async updateVoiceNote(): Promise<void> {
    const uniqueAudioTemplateName = this.getUniqueAudioTemplateName();
    this.dialog.open(VoiceNoteAddDialog, { data: { audioTemplateName: uniqueAudioTemplateName } })
      .afterClosed().subscribe(async (result: any) => {
        if (!result) return;
        this.node.audioTemplateName = result;
      });
  }

  onChangeOnHold(index: number) {
    if (this.node.changes[index].onHold) {
      this.node.changes[index].onHoldDurationInDays = 7;
    } else {
      delete this.node.changes[index].onHoldDurationInDays;
    }
  }

  private getUniqueAudioTemplateName(): string {
    const uniqueAudioTemplateIds = [this.regimenId, this.node.name, this.level];
    if (this.variantId !== ApiClientConstant.Regimen.SelectedVariantId.variant_1499) {
      uniqueAudioTemplateIds.push(this.variantId);
    }
    return this.node.audioTemplateName || uniqueAudioTemplateIds.join('_');
  }
}
